/**
 * Facebook Messenger Webhook Events - Main Entry Point
 * 
 * This file provides the unified type system for all webhook events supported 
 * by the Messenger Platform. It uses discriminated unions with proper type
 * narrowing to ensure type safety.
 * 
 * @module webhooks/webhook-events
 */

import {
  WebhookEventType,
  WebhookPayload as GenericWebhookPayload,
  extractEvents,
} from './base-types';

import {
  MessageEditWebhookEvent,
  MessageEditWebhookPayload,
} from './message-edits';

import {
  MessageReactionWebhookEvent,
  MessageReactionWebhookPayload,
} from './message-reactions';

import {
  MessageWebhookEvent,
  MessageWebhookPayload,
} from './messages';

import {
  MessageEchoWebhookEvent,
  MessageEchoWebhookPayload,
} from './message-echoes';

import {
  MessageReadsWebhookEvent,
  MessageReadsWebhookPayload,
} from './message-reads';

import {
  MessagingFeedbackWebhookEvent,
  MessagingFeedbackWebhookPayload,
} from './messaging-feedback';

import {
  MessagingPostbackWebhookEvent,
  MessagingPostbackWebhookPayload,
} from './messaging-postbacks';

// Re-export base types for convenience
export {
  WebhookEventType,
  WebhookSender,
  WebhookRecipient,
  WebhookEntry,
  WebhookPayload,
} from './base-types';

// Re-export GenericWebhookPayload as an alias
export type { WebhookPayload as GenericWebhookPayload } from './base-types';

/**
 * Discriminated union type of all possible webhook events.
 * Each event has a 'type' field that allows TypeScript to narrow the type.
 */
export type MessengerWebhookEvent =
  | MessageEditWebhookEvent
  | MessageReactionWebhookEvent
  | MessageWebhookEvent
  | MessageEchoWebhookEvent
  | MessageReadsWebhookEvent
  | MessagingFeedbackWebhookEvent
  | MessagingPostbackWebhookEvent;

/**
 * Union type of all possible webhook payloads
 */
export type MessengerWebhookPayload =
  | MessageEditWebhookPayload
  | MessageReactionWebhookPayload
  | MessageWebhookPayload
  | MessageEchoWebhookPayload
  | MessageReadsWebhookPayload
  | MessagingFeedbackWebhookPayload
  | MessagingPostbackWebhookPayload;

/**
 * Type guard to check webhook event type for a SINGLE event.
 * This function now properly handles individual events from the messaging array.
 * 
 * @param event - A single webhook event from the messaging array
 * @returns The event type or null if unknown
 * 
 * @example
 * ```typescript
 * const events = extractWebhookEvents(payload);
 * for (const event of events) {
 *   const eventType = getWebhookEventType(event);
 *   console.log(`Event type: ${eventType}`);
 * }
 * ```
 */
export function getWebhookEventType(event: MessengerWebhookEvent | any): WebhookEventType | null {
  if (!event || typeof event !== 'object') {
    return null;
  }

  // Check for discriminator field if it exists (new structure)
  if ('type' in event && Object.values(WebhookEventType).includes(event.type)) {
    return event.type as WebhookEventType;
  }

  // Fallback to property-based detection for backward compatibility
  if (
    'message' in event &&
    event.message &&
    typeof event.message === 'object' &&
    event.message.is_echo === true
  ) {
    return WebhookEventType.MESSAGE_ECHO;
  }
  if ('message' in event) {
    return WebhookEventType.MESSAGE;
  }
  if ('message_edit' in event) {
    return WebhookEventType.MESSAGE_EDIT;
  }
  if ('reaction' in event) {
    return WebhookEventType.MESSAGE_REACTION;
  }
  if ('read' in event) {
    return WebhookEventType.MESSAGE_READ;
  }
  if ('messaging_feedback' in event) {
    return WebhookEventType.MESSAGING_FEEDBACK;
  }
  if ('postback' in event) {
    return WebhookEventType.MESSAGING_POSTBACK;
  }

  return null;
}

/**
 * Extract event types from a complete webhook payload.
 * This is the correct function to use when processing the full webhook payload.
 * 
 * @param payload - The complete webhook payload from Facebook
 * @returns Array of unique event types found in the payload
 * 
 * @example
 * ```typescript
 * app.post('/webhook', (req, res) => {
 *   const payload = req.body;
 *   const eventTypes = getWebhookPayloadEventTypes(payload);
 *   
 *   if (eventTypes.includes(WebhookEventType.MESSAGE)) {
 *     // Process message events
 *   }
 * });
 * ```
 */
export function getWebhookPayloadEventTypes(payload: GenericWebhookPayload): WebhookEventType[] {
  const eventTypes = new Set<WebhookEventType>();
  
  if (payload.object === 'page' && Array.isArray(payload.entry)) {
    for (const entry of payload.entry) {
      if (Array.isArray(entry.messaging)) {
        for (const event of entry.messaging) {
          const type = getWebhookEventType(event);
          if (type) {
            eventTypes.add(type);
          }
        }
      }
    }
  }
  
  return Array.from(eventTypes);
}

/**
 * Extract all events from a webhook payload.
 * This properly extracts individual events from the nested structure.
 * 
 * @param payload - The complete webhook payload
 * @returns Array of individual webhook events
 * 
 * @example
 * ```typescript
 * const events = extractWebhookEvents(payload);
 * events.forEach(event => {
 *   const type = getWebhookEventType(event);
 *   console.log(`Processing ${type} event`);
 * });
 * ```
 */
export function extractWebhookEvents(payload: GenericWebhookPayload): MessengerWebhookEvent[] {
  return extractEvents(payload) as MessengerWebhookEvent[];
}

/**
 * Add discriminator to events that don't have it (for backward compatibility).
 * This ensures all events have the 'type' field for proper type narrowing.
 * 
 * @param event - The webhook event to enhance
 * @returns The event with added type discriminator
 */
function addDiscriminator(event: any): MessengerWebhookEvent | null {
  const type = getWebhookEventType(event);
  if (!type) return null;
  
  // Add the type field if it doesn't exist
  if (!('type' in event)) {
    return { ...event, type } as MessengerWebhookEvent;
  }
  
  return event as MessengerWebhookEvent;
}

/**
 * Process webhook events by type with proper type narrowing.
 * This uses the discriminated union for automatic type narrowing.
 * 
 * @param payload - The complete webhook payload
 * @param handlers - Object with handler functions for each event type
 * 
 * @example
 * ```typescript
 * processWebhookEvents(payload, {
 *   onMessage: async (event) => {
 *     // TypeScript knows event is MessageWebhookEvent
 *     console.log(`Message: ${event.message.text}`);
 *   },
 *   onMessageEdit: async (event) => {
 *     // TypeScript knows event is MessageEditWebhookEvent
 *     console.log(`Edited to: ${event.message_edit.text}`);
 *   }
 * });
 * ```
 */
export interface WebhookEventHandlers {
  onMessage?: (event: MessageWebhookEvent) => void | Promise<void>;
  onMessageEcho?: (event: MessageEchoWebhookEvent) => void | Promise<void>;
  onMessageEdit?: (event: MessageEditWebhookEvent) => void | Promise<void>;
  onMessageReaction?: (event: MessageReactionWebhookEvent) => void | Promise<void>;
  onMessageRead?: (event: MessageReadsWebhookEvent) => void | Promise<void>;
  onMessagingFeedback?: (event: MessagingFeedbackWebhookEvent) => void | Promise<void>;
  onMessagingPostback?: (event: MessagingPostbackWebhookEvent) => void | Promise<void>;
  onUnknown?: (event: any) => void | Promise<void>;
}

export async function processWebhookEvents(
  payload: GenericWebhookPayload,
  handlers: WebhookEventHandlers
): Promise<void> {
  const events = extractWebhookEvents(payload);
  
  for (const rawEvent of events) {
    // Ensure event has discriminator
    const event = addDiscriminator(rawEvent);
    if (!event) {
      if (handlers.onUnknown) {
        await handlers.onUnknown(rawEvent);
      }
      continue;
    }
    
    // Use discriminated union for type narrowing
    switch (event.type) {
      case WebhookEventType.MESSAGE:
        if (handlers.onMessage) {
          await handlers.onMessage(event);
        }
        break;

      case WebhookEventType.MESSAGE_ECHO:
        if (handlers.onMessageEcho) {
          await handlers.onMessageEcho(event);
        }
        break;
        
      case WebhookEventType.MESSAGE_EDIT:
        if (handlers.onMessageEdit) {
          await handlers.onMessageEdit(event);
        }
        break;
        
      case WebhookEventType.MESSAGE_REACTION:
        if (handlers.onMessageReaction) {
          await handlers.onMessageReaction(event);
        }
        break;
        
      case WebhookEventType.MESSAGE_READ:
        if (handlers.onMessageRead) {
          await handlers.onMessageRead(event);
        }
        break;
        
      case WebhookEventType.MESSAGING_FEEDBACK:
        if (handlers.onMessagingFeedback) {
          await handlers.onMessagingFeedback(event);
        }
        break;
        
      case WebhookEventType.MESSAGING_POSTBACK:
        if (handlers.onMessagingPostback) {
          await handlers.onMessagingPostback(event);
        }
        break;
        
      default:
        // This should never happen with proper typing
        const exhaustiveCheck: never = event;
        if (handlers.onUnknown) {
          await handlers.onUnknown(exhaustiveCheck);
        }
        break;
    }
  }
}

/**
 * Type predicate functions for each event type.
 * These enable proper type narrowing in conditional statements.
 */

export function isMessageEvent(event: MessengerWebhookEvent): event is MessageWebhookEvent {
  return event.type === WebhookEventType.MESSAGE || 'message' in event;
}

export function isMessageEchoEvent(event: MessengerWebhookEvent): event is MessageEchoWebhookEvent {
  return (
    event.type === WebhookEventType.MESSAGE_ECHO ||
    ('message' in event && (event as any).message && (event as any).message.is_echo === true)
  );
}

export function isMessageEditEvent(event: MessengerWebhookEvent): event is MessageEditWebhookEvent {
  return event.type === WebhookEventType.MESSAGE_EDIT || 'message_edit' in event;
}

export function isMessageReactionEvent(event: MessengerWebhookEvent): event is MessageReactionWebhookEvent {
  return event.type === WebhookEventType.MESSAGE_REACTION || 'reaction' in event;
}

export function isMessageReadEvent(event: MessengerWebhookEvent): event is MessageReadsWebhookEvent {
  return event.type === WebhookEventType.MESSAGE_READ || 'read' in event;
}

export function isMessagingFeedbackEvent(event: MessengerWebhookEvent): event is MessagingFeedbackWebhookEvent {
  return event.type === WebhookEventType.MESSAGING_FEEDBACK || 'messaging_feedback' in event;
}

export function isMessagingPostbackEvent(event: MessengerWebhookEvent): event is MessagingPostbackWebhookEvent {
  return event.type === WebhookEventType.MESSAGING_POSTBACK || 'postback' in event;
}

/**
 * Webhook verification parameters for subscription verification
 */
export interface WebhookVerificationParams {
  'hub.mode': string;
  'hub.verify_token': string;
  'hub.challenge': string;
}

/**
 * Webhook signature verification result
 */
export interface WebhookSignatureVerificationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Verify webhook subscription during initial setup
 * 
 * @param params - Query parameters from Facebook's verification request
 * @param verifyToken - Your webhook verify token
 * @returns The challenge string if valid, null if invalid
 * 
 * @example
 * ```typescript
 * app.get('/webhook', (req, res) => {
 *   const challenge = verifyWebhookSubscription(req.query, process.env.VERIFY_TOKEN);
 *   if (challenge) {
 *     res.send(challenge);
 *   } else {
 *     res.status(403).send('Forbidden');
 *   }
 * });
 * ```
 */
export function verifyWebhookSubscription(
  params: WebhookVerificationParams,
  verifyToken: string
): string | null {
  if (
    params['hub.mode'] === 'subscribe' &&
    params['hub.verify_token'] === verifyToken
  ) {
    return params['hub.challenge'];
  }
  return null;
}

/**
 * Verify webhook signature from Facebook
 * 
 * **Note**: This function requires Node.js crypto module and is only supported in server-side environments.
 * For browser environments, you should handle signature verification on your backend.
 * 
 * @param rawBody - The raw request body as Buffer
 * @param signature - The X-Hub-Signature-256 header value from Facebook
 * @param appSecret - Your Facebook app secret
 * @returns Promise that resolves to verification result with validity and optional error message
 * 
 * @example
 * ```typescript
 * import express from 'express';
 * import { verifyWebhookSignature } from '@warriorteam/messenger-sdk';
 * 
 * app.post('/webhook', express.raw({type: 'application/json'}), async (req, res) => {
 *   const signature = req.get('X-Hub-Signature-256');
 *   const result = await verifyWebhookSignature(req.body, signature, process.env.APP_SECRET);
 *   
 *   if (!result.isValid) {
 *     return res.status(401).json({error: result.error});
 *   }
 *   
 *   // Process webhook...
 *   const payload = JSON.parse(req.body.toString());
 *   // Handle webhook events...
 * });
 * ```
 */
export async function verifyWebhookSignature(
  rawBody: Buffer,
  signature: string | undefined,
  appSecret: string
): Promise<WebhookSignatureVerificationResult> {
  if (!signature) {
    return {
      isValid: false,
      error: 'Missing X-Hub-Signature-256 header'
    };
  }

  if (!signature.startsWith('sha256=')) {
    return {
      isValid: false,
      error: 'Invalid signature format. Expected sha256= prefix'
    };
  }

  if (!appSecret) {
    return {
      isValid: false,
      error: 'App secret is required for signature verification'
    };
  }

  try {
    // Check if we're in a Node.js environment
    if (typeof Buffer === 'undefined') {
      return {
        isValid: false,
        error: 'Signature verification is only supported in Node.js environments'
      };
    }

    // Use dynamic import for ES6 compatibility
    const crypto = await import('crypto');
    
    // Extract the hash from the signature
    const receivedHash = signature.substring(7); // Remove 'sha256=' prefix
    
    // Compute the expected signature
    const expectedSignature = crypto
      .createHmac('sha256', appSecret)
      .update(rawBody)
      .digest('hex');

    // Use timing-safe comparison to prevent timing attacks
    const receivedBuffer = Buffer.from(receivedHash, 'hex');
    const expectedBuffer = Buffer.from(expectedSignature, 'hex');

    if (receivedBuffer.length !== expectedBuffer.length) {
      return {
        isValid: false,
        error: 'Signature length mismatch'
      };
    }

    const isValid = crypto.timingSafeEqual(receivedBuffer, expectedBuffer);
    
    return {
      isValid,
      error: isValid ? undefined : 'Signature verification failed'
    };

  } catch (error) {
    return {
      isValid: false,
      error: `Signature verification error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Helper to check if a payload contains specific event types
 * 
 * @param payload - The webhook payload
 * @param eventTypes - Array of event types to check for
 * @returns True if payload contains any of the specified event types
 * 
 * @example
 * ```typescript
 * if (payloadContainsEventTypes(payload, [WebhookEventType.MESSAGE, WebhookEventType.MESSAGING_POSTBACK])) {
 *   // Process message or postback events
 * }
 * ```
 */
export function payloadContainsEventTypes(
  payload: GenericWebhookPayload,
  eventTypes: WebhookEventType[]
): boolean {
  const foundTypes = getWebhookPayloadEventTypes(payload);
  return eventTypes.some(type => foundTypes.includes(type));
}

/**
 * Filter events by type from a webhook payload
 * 
 * @param payload - The webhook payload
 * @param eventType - The event type to filter for
 * @returns Array of events of the specified type
 * 
 * @example
 * ```typescript
 * const messageEvents = filterEventsByType(payload, WebhookEventType.MESSAGE);
 * messageEvents.forEach(event => {
 *   console.log(`Message: ${event.message.text}`);
 * });
 * ```
 */
export function filterEventsByType<T extends MessengerWebhookEvent>(
  payload: GenericWebhookPayload,
  eventType: WebhookEventType
): T[] {
  const allEvents = extractWebhookEvents(payload);
  return allEvents.filter(event => {
    const type = getWebhookEventType(event);
    return type === eventType;
  }) as T[];
}